import type { ResolvedNode, ResolvedSegment } from "../resolve/resolved.js";
import type { BlockRegistry } from "../registry/registry.js";
import type { RenderContext, RenderMode, BindingInfo } from "../registry/block-definition.js";
import type { Theme } from "../types/theme.js";
import type { DocumentStyles } from "../types/template.js";
import type { PageContext } from "../types/data.js";
import type { FormatContext } from "../expr/format.js";
import { h, type VChild, type VNode, type VProps } from "../vnode/h.js";
import { styleToCss, cssText } from "../theme/resolve-style.js";
import { Scope, evaluate } from "../expr/evaluate.js";
import { toDisplayString } from "../expr/format.js";
import type { RichTextDoc, InlineNode, TextMarks, Paragraph } from "../richtext/model.js";
import { isSafeUrl } from "../validate/validate.js";
import { resolveValue } from "../theme/tokens.js";

export interface RenderTreeOptions {
  registry: BlockRegistry;
  theme: Theme;
  styles: DocumentStyles;
  mode: RenderMode;
  page: PageContext;
  format: FormatContext;
  /** Content width in px at the root. */
  width: number;
  /** Hook for editors to add attributes to node wrappers. */
  decorate?: (node: ResolvedNode, props: VProps) => VProps;
}

function deferredScope(opts: RenderTreeOptions): Scope {
  return new Scope({
    page: opts.page,
    now: opts.format.now ?? new Date(),
    theme: { name: opts.theme.name, logo: opts.theme.logo, colors: opts.theme.colors, typography: opts.theme.typography, meta: opts.theme.meta ?? {} }
  });
}

function evalDeferred(expr: string, opts: RenderTreeOptions): string {
  try {
    return toDisplayString(evaluate(expr, { scope: deferredScope(opts), format: opts.format }));
  } catch {
    return "";
  }
}

/** Design-mode chip for a data token. */
export function tokenChip(expr: string, label: string | undefined, value?: string): VNode {
  return h("span", { class: "bp-token", "data-expr": expr, title: expr, contenteditable: "false" }, label ?? expr, value !== undefined && value !== "" ? h("span", { class: "bp-token__value" }, value) : null);
}

export function renderSegments(segments: ResolvedSegment[] | undefined, fallback: string | undefined, opts: RenderTreeOptions): VChild[] {
  if (!segments) return fallback !== undefined ? [fallback] : [];
  return segments.map((s) => {
    if (s.kind === "text") return s.text;
    const value = s.deferred ? evalDeferred(s.expr!, opts) : s.value ?? "";
    if (opts.mode === "design") return tokenChip(s.expr!, s.label, s.deferred ? value : undefined);
    return value;
  });
}

function markWrap(children: VChild[], marks: TextMarks | undefined, opts: RenderTreeOptions): VChild {
  if (!marks) return children;
  let out: VChild = children;
  if (marks.code) out = h("code", { class: "bp-code" }, out);
  if (marks.bold) out = h("strong", null, out);
  if (marks.italic) out = h("em", null, out);
  if (marks.underline) out = h("u", null, out);
  if (marks.strike) out = h("s", null, out);
  if (marks.color) out = h("span", { style: { color: resolveValue(marks.color, opts.theme, "inherit") } }, out);
  if (marks.link) {
    const href = marks.link;
    const safe = isSafeUrl(href);
    const linkColor = resolveValue(opts.styles.link.color, opts.theme, opts.theme.colors.secondary);
    out = h("a", { class: "bp-link", href: safe ? href : undefined, rel: "noopener", style: { color: linkColor, "text-decoration": opts.styles.link.underline === false ? "none" : "underline" } }, out);
  }
  return out;
}

export function renderInline(content: InlineNode[], opts: RenderTreeOptions): VChild[] {
  return content.map((n) => {
    if (n.type === "break") return h("br");
    if (n.type === "text") return markWrap([n.text], n.marks, opts);
    const value = n.deferred ? evalDeferred(n.expr, opts) : n.value ?? "";
    const inner = opts.mode === "design" ? tokenChip(n.expr, n.label, n.deferred ? value : undefined) : value;
    return markWrap([inner], n.marks, opts);
  });
}

function renderParagraph(p: Paragraph, opts: RenderTreeOptions): VNode {
  const empty = p.content.length === 0;
  return h("p", { class: ["bp-p", empty && "bp-p--empty"], style: p.align ? { "text-align": p.align } : undefined }, empty ? h("br") : renderInline(p.content, opts));
}

export function renderRichText(doc: RichTextDoc | undefined, opts: RenderTreeOptions): VChild[] {
  if (!doc) return [];
  return doc.content.map((b) => {
    if (b.type === "paragraph") return renderParagraph(b, opts);
    return h(
      b.type === "bulletList" ? "ul" : "ol",
      { class: "bp-list" },
      b.content.map((li) => h("li", null, li.content.map((p) => renderParagraph(p, opts))))
    );
  });
}

export function nodeAttrs(node: ResolvedNode, extra: VProps = {}): VProps {
  return {
    "data-key": node.key,
    "data-node": node.nodeId,
    "data-type": node.type,
    ...extra
  };
}

/** Width available to a column inside a row. */
export function columnWidth(rowWidth: number, span: number, gap: number, columns: number): number {
  const inner = rowWidth - gap * Math.max(0, columns - 1);
  return Math.max(0, (inner * span) / 12);
}

/**
 * Renders a resolved node (and its subtree) to a VNode. This is the single
 * rendering path used by the editor canvas, the preview and the PDF service.
 */
export function renderNode(node: ResolvedNode, opts: RenderTreeOptions, width: number = opts.width): VNode | null {
  const def = opts.registry.get(node.type);
  const gap = opts.styles.spacing.blockGap;
  const columnGap = opts.styles.spacing.columnGap;

  // Children first (containers)
  let childWidth = width;
  if (def?.container) {
    const pad = node.style.padding;
    const bw = node.style.border ? node.style.border.width : 0;
    childWidth = Math.max(0, width - pad.left - pad.right - 2 * bw);
  }
  const children: VNode[] = [];
  if (node.role === "row") {
    const count = node.children.length;
    for (const col of node.children) {
      const w = columnWidth(childWidth, col.span ?? 12 / Math.max(1, count), columnGap, count);
      const v = renderNode(col, opts, w);
      if (v) children.push(v);
    }
  } else {
    for (const c of node.children) {
      const v = renderNode(c, opts, childWidth);
      if (v) children.push(v);
    }
  }

  const s = node.style;
  const css = styleToCss(s);
  delete css["margin"];
  delete css["width"];
  delete css["height"];

  const wrapperStyle: Record<string, string | undefined> = {};
  const m = s.margin;
  if (m.top || m.right || m.bottom || m.left) wrapperStyle["margin"] = `${m.top}px ${m.right}px ${m.bottom}px ${m.left}px`;
  if (s.width !== undefined && node.role === "block") {
    wrapperStyle["width"] = typeof s.width === "number" ? `${s.width}px` : s.width;
    wrapperStyle["max-width"] = "100%";
    if (s.align === "center") wrapperStyle["margin-left"] = wrapperStyle["margin-right"] = "auto";
    else if (s.align === "right") wrapperStyle["margin-left"] = "auto";
  }
  if (s.height !== undefined && node.role === "block") wrapperStyle["height"] = `${s.height}px`;
  if (node.role === "column") {
    wrapperStyle["grid-column"] = `span ${node.span ?? 12}`;
    wrapperStyle["min-width"] = "0";
  }

  const classes = ["bp-node", `bp-node--${node.type}`, `bp-${node.role}`];
  if (node.props.__hiddenByCondition) classes.push("bp-node--hidden");
  if (node.placeholder) classes.push("bp-node--placeholder");
  if (node.locked) classes.push("bp-node--locked");
  if (node.iteration) classes.push("bp-repeater-item");
  if (node.flow.keepTogether) classes.push("bp-node--keep");

  let attrs: VProps = nodeAttrs(node, { class: classes, style: wrapperStyle });
  if (opts.decorate) attrs = opts.decorate(node, attrs);

  if (!def) {
    return h("div", attrs, h("div", { class: "bp-unknown-block", style: cssText(css) }, `Unavailable block: ${node.type}`));
  }

  const ctx: RenderContext = {
    node,
    props: node.props,
    style: s,
    css,
    theme: opts.theme,
    styles: opts.styles,
    mode: opts.mode,
    h,
    children,
    page: opts.page,
    width,
    format: opts.format,
    registry: opts.registry,
    text: (prop, fallback) => renderSegments(node.textSegments[prop], fallback ?? (typeof node.props[prop] === "string" ? (node.props[prop] as string) : undefined), opts),
    richText: (prop) => renderRichText(node.richText[prop] ?? (node.props[prop] as RichTextDoc | undefined), opts),
    textValue: (value) => (value === undefined ? [] : [value]),
    binding: (prop) => node.bindings[prop] as BindingInfo | undefined,
    partAttrs: (part, extra = {}) => ({ "data-part": part, "data-node": node.nodeId, ...extra }),
    placeholder: (text, extra = {}) => h("div", { class: "bp-placeholder", ...extra }, text)
  };

  let inner: VNode | null;
  try {
    inner = def.render(ctx);
  } catch (e) {
    inner = h("div", { class: "bp-block-error" }, `${def.name} failed to render: ${(e as Error).message}`);
  }
  if (!inner) return null;

  // Containers: lay out children in a column with the document block gap.
  if (def.container && node.role !== "row" && node.role !== "column" && def.container.direction !== "row") {
    // Blocks render their own children; nothing to add here.
  }
  void gap;
  return h("div", attrs, inner);
}

/** Renders a list of resolved sections into a fragment-less flat list. */
export function renderSections(sections: ResolvedNode[], opts: RenderTreeOptions): VNode[] {
  const out: VNode[] = [];
  for (const s of sections) {
    const v = renderNode(s, opts, opts.width);
    if (v) out.push(v);
  }
  return out;
}
